/**
 * Spjall (/chat): the chats I may read — the choir chat and my voice first, then (conductor, editors,
 * admins) the other voices — with the newest message, its time and my unread count.
 */
import { BellOff, ChevronRight, MessagesSquare, Users, WifiOff } from 'lucide-react';
import { Link } from 'react-router';
import type { ChatSummaryDTO } from '../../shared/dto/chat';
import { useServerNow } from '../api/calendar';
import { useChats } from '../api/chat';
import { useRequiredMe } from '../auth/AuthProvider';
import { Page } from '../components/layout';
import { SECTION_COLOR } from '../components/people';
import { CenteredSpinner, EmptyState, ErrorBox } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { formatRelativeTime } from '../lib/dates';
import { badgeText, displayNames } from '../notify/format';
import { chatName } from './labels';

/** Round chat icon: people for the choir chat, the voice's colour and short name for a voice chat. */
export function ChatIcon({ id, size = 52 }: { id: ChatSummaryDTO['id']; size?: number }) {
  const t = useT();
  if (id === 'choir') {
    return (
      <span className="inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]" style={{ width: size, height: size }} aria-hidden>
        <Users className="size-6" />
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full text-[17px] font-bold text-white"
      style={{ width: size, height: size, background: SECTION_COLOR[id] }}
      aria-hidden
    >
      {t(`section.short.${id}`)}
    </span>
  );
}

function ChatRow({ c, meId, now }: { c: ChatSummaryDTO; meId: string; now: number }) {
  const t = useT();
  const { loc } = useI18n();
  const name = chatName(c.id, t);
  const who = c.last ? (c.last.authorId === meId ? t('chat.you') : displayNames([c.last.authorName])[0]) : null;
  return (
    <li>
      <Link
        to={`/chat/${c.id}`}
        className="flex min-h-20 items-center gap-4 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4 transition hover:border-[var(--accent)] active:bg-[var(--accent-soft)]"
      >
        <ChatIcon id={c.id} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-[18px] font-semibold">{name}</span>
            {c.muted ? <BellOff className="size-4 shrink-0 text-[var(--muted)]" aria-label={t('chat.muted')} /> : null}
          </span>
          <span className={c.unread > 0 ? 'block truncate text-[15px] font-semibold text-[var(--ink)]' : 'block truncate text-[15px] text-[var(--muted)]'}>
            {c.last ? `${who}: ${c.last.body}` : t('chat.list.no_messages')}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {c.last ? <span className="text-[13px] text-[var(--muted)]">{formatRelativeTime(c.last.createdAt, now, loc)}</span> : null}
          {c.unread > 0 ? (
            <span
              className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[var(--danger)] px-1.5 text-[13px] font-bold text-white"
              aria-label={t('chat.unread', { count: c.unread })}
            >
              {badgeText(c.unread)}
            </span>
          ) : (
            <ChevronRight className="size-5 text-[var(--muted)]" aria-hidden />
          )}
        </span>
      </Link>
    </li>
  );
}

export default function ChatListScreen() {
  const t = useT();
  const me = useRequiredMe();
  const q = useChats();
  const now = useServerNow(30_000);
  const list = q.data ?? [];
  const mine = list.filter((c) => c.own);
  const others = list.filter((c) => !c.own);

  let body;
  if (q.data) {
    body = (
      <>
        <ul className="flex flex-col gap-3" aria-label={t('chat.list.mine')}>
          {mine.map((c) => (
            <ChatRow key={c.id} c={c} meId={me.id} now={now} />
          ))}
        </ul>
        {others.length > 0 ? (
          <section className="mt-8" aria-labelledby="chat-others">
            <h2 id="chat-others" className="text-[20px] font-bold">
              {t('chat.list.others')}
            </h2>
            <p className="mb-3 text-[15px] text-[var(--muted)]">{t('chat.list.others_hint')}</p>
            <ul className="flex flex-col gap-3">
              {others.map((c) => (
                <ChatRow key={c.id} c={c} meId={me.id} now={now} />
              ))}
            </ul>
          </section>
        ) : null}
        <p className="mt-8 flex items-start gap-2 text-[15px] text-[var(--muted)]">
          <MessagesSquare className="mt-0.5 size-5 shrink-0" aria-hidden />
          {t('chat.list.help')}
        </p>
      </>
    );
  } else if (q.isPending && q.fetchStatus === 'paused') {
    body = (
      <EmptyState icon={WifiOff} title={t('common.offline')}>
        {t('common.no_data_offline')}
      </EmptyState>
    );
  } else if (q.isPending) body = <CenteredSpinner label={t('common.loading')} />;
  else
    body = (
      <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
        {t('chat.list.load_error')}
      </ErrorBox>
    );

  return <Page title={t('chat.title')}>{body}</Page>;
}
